import React, { useEffect, useMemo, useState } from "react";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { useTagManagement } from "../../hooks/useTagManagement";
import { OutreachCategory, Tag } from "../../types/outreach";
import { type CreateTagRequest } from "../../services/outreach/firestore/tagService";

const filter = createFilterOptions<TagOption>();

type TagOption = Tag | TagCreationOption;

interface TagCreationOption {
  inputValue: string;
  name: string;
  isNew: boolean;
}

const isTagOption = (option: TagOption): option is Tag => {
  return (option as Tag).id !== undefined;
};

const isCreationOption = (option: TagOption): option is TagCreationOption => {
  return (option as TagCreationOption).isNew === true;
};

export interface TagAutocompleteProps {
  value: string | Tag | null;
  onChange: (value: string | null, tag?: Tag | null) => void;
  category?: OutreachCategory | "global";
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  error?: boolean;
  includeGlobal?: boolean;
  allowCreate?: boolean;
  disableClearable?: boolean;
  textFieldProps?: Partial<TextFieldProps>;
  onTagCreated?: (tag: Tag) => void;
}

export const TagAutocomplete: React.FC<TagAutocompleteProps> = ({
  value,
  onChange,
  category,
  label = "Tag",
  placeholder = "Select or create a tag",
  helperText,
  error = false,
  includeGlobal = true,
  allowCreate = true,
  disableClearable = false,
  textFieldProps,
  onTagCreated,
}) => {
  const {
    tags,
    popularTags,
    loading,
    popularLoading,
    error: tagError,
    mutating,
    searchTags,
    createTag,
    fetchPopularTags,
  } = useTagManagement({ category, includeGlobal });

  const [inputValue, setInputValue] = useState<string>("");
  const [searching, setSearching] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<Tag[]>([]);
  const [creationError, setCreationError] = useState<string | null>(null);

  const combinedTags = useMemo(() => {
    const map = new Map<string, Tag>();
    [...popularTags, ...tags, ...searchResults].forEach((tag) => {
      if (!map.has(tag.id)) {
        map.set(tag.id, tag);
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [popularTags, tags, searchResults]);

  const resolvedValue = useMemo<TagOption | null>(() => {
    if (!value) {
      return null;
    }

    if (typeof value === "string") {
      const byId = combinedTags.find((option) => option.id === value);
      if (byId) {
        return byId;
      }
      const byName = combinedTags.find(
        (option) => option.name.toLowerCase() === value.toLowerCase()
      );
      if (byName) {
        return byName;
      }
      return {
        inputValue: value,
        name: value,
        isNew: false,
      };
    }

    const match = combinedTags.find((option) => option.id === value.id);
    return match ?? value;
  }, [combinedTags, value]);

  useEffect(() => {
    fetchPopularTags(category, 5);
  }, [category, fetchPopularTags]);

  useEffect(() => {
    if (!allowCreate) {
      setSearchResults([]);
      return;
    }

    if (!inputValue || inputValue.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    let isActive = true;
    setSearching(true);
    setCreationError(null);

    const timeout = setTimeout(() => {
      searchTags(inputValue, { category, includeGlobal, limit: 10 })
        .then((results) => {
          if (!isActive) return;
          setSearchResults(results);
        })
        .catch(() => {
          if (!isActive) return;
          setSearchResults([]);
        })
        .finally(() => {
          if (!isActive) return;
          setSearching(false);
        });
    }, 250);

    return () => {
      isActive = false;
      clearTimeout(timeout);
    };
  }, [allowCreate, category, includeGlobal, inputValue, searchTags]);

  const handleCreateTag = async (tagName: string) => {
    const trimmed = tagName.trim();
    if (!trimmed) {
      return;
    }

    const payload: CreateTagRequest = {
      name: trimmed,
      category: category ?? "global",
    };

    try {
      const newTag = await createTag(payload);
      setCreationError(null);
      onChange(newTag.name, newTag);
      onTagCreated?.(newTag);
      setInputValue("");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to create tag";
      setCreationError(message);
    }
  };

  const combinedError = creationError || tagError?.message || undefined;

  return (
    <Autocomplete<TagOption, false, boolean, boolean>
      value={resolvedValue}
      onChange={(event, newValue) => {
        if (newValue === null) {
          onChange(null, null);
          setInputValue("");
          return;
        }

        if (typeof newValue === "string") {
          if (allowCreate) {
            handleCreateTag(newValue);
          } else {
            onChange(newValue, null);
          }
          setInputValue("");
          return;
        }

        if (isCreationOption(newValue)) {
          if (allowCreate) {
            handleCreateTag(newValue.inputValue);
          }
          setInputValue("");
          return;
        }

        if (isTagOption(newValue)) {
          setCreationError(null);
          onChange(newValue.name, newValue);
          setInputValue("");
        }
      }}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);

        const { inputValue: paramsInputValue } = params;
        if (
          allowCreate &&
          paramsInputValue !== "" &&
          !options.some(
            (option) =>
              isTagOption(option) &&
              option.name.toLowerCase() === paramsInputValue.toLowerCase()
          )
        ) {
          filtered.push({
            inputValue: paramsInputValue,
            name: `Add "${paramsInputValue}"`,
            isNew: true,
          });
        }

        return filtered;
      }}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      options={combinedTags}
      getOptionLabel={(option) => {
        if (typeof option === "string") {
          return option;
        }
        if (isCreationOption(option)) {
          return option.inputValue;
        }
        return option.name;
      }}
      isOptionEqualToValue={(option, optionValue) => {
        if (isCreationOption(option) && isCreationOption(optionValue)) {
          return option.inputValue === optionValue.inputValue;
        }
        if (isCreationOption(option) || isCreationOption(optionValue)) {
          return false;
        }
        const optionId = isTagOption(option) ? option.id : undefined;
        const valueId = isTagOption(optionValue) ? optionValue.id : undefined;
        if (optionId && valueId) {
          return optionId === valueId;
        }
        const optionName = isTagOption(option) ? option.name : undefined;
        const valueName = isTagOption(optionValue) ? optionValue.name : undefined;
        return !!optionName && !!valueName && optionName === valueName;
      }}
      freeSolo={allowCreate}
      includeInputInList
      disableClearable={disableClearable}
      loading={loading || mutating || searching || popularLoading}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error || Boolean(combinedError)}
          helperText={combinedError || helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {(loading || mutating || searching || popularLoading) && (
                  <CircularProgress color="inherit" size={20} />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          {...textFieldProps}
        />
      )}
      noOptionsText={allowCreate ? "Type to create a tag" : "No tags found"}
    />
  );
};

export default TagAutocomplete;
